import { useState } from 'react'

import { Predict } from './common'

/**
 * Какое это явление? (уроки 1–2): сложное явление природы физика раскладывает на физические
 * явления разных видов. Ученик относит каждое явление к виду; молнию — сразу к нескольким.
 */

type Вид = 'механическое' | 'тепловое' | 'звуковое' | 'световое' | 'электромагнитное'

const ВИДЫ: readonly Вид[] = ['механическое', 'тепловое', 'звуковое', 'световое', 'электромагнитное']

const РАЗДЕЛ: Record<Вид, string> = {
  механическое: 'механика',
  тепловое: 'термодинамика',
  звуковое: 'акустика',
  световое: 'оптика',
  электромагнитное: 'электродинамика',
}

const ЯВЛЕНИЯ: { что: string; вид: Вид; почему: string }[] = [
  { что: 'Футбольный мяч летит в окно', вид: 'механическое', почему: 'меняется положение мяча' },
  { что: 'Солнце отражается в луже', вид: 'световое', почему: 'это происходит со светом' },
  { что: 'Автомобиль трогается с места', вид: 'механическое', почему: 'тело начинает двигаться' },
  { что: 'Река осенью замерзает', вид: 'тепловое', почему: 'вода остывает и переходит в лёд' },
  { что: 'Гвоздь тонет в воде', вид: 'механическое', почему: 'меняется положение гвоздя' },
  { что: 'Электрический вентилятор вращается', вид: 'механическое', почему: '«вентилятор вращается» — главное здесь движение' },
  { что: 'Мальчик зажигает спичку', вид: 'тепловое', почему: 'спичка нагревается и горит' },
  { что: 'Гремит гром', вид: 'звуковое', почему: 'это звук' },
  { что: 'Магнит притягивает скрепку', вид: 'электромагнитное', почему: 'действует магнитное поле' },
  { что: 'Лампочка светится', вид: 'световое', почему: 'тело испускает свет' },
]

/** Молния — явление природы, в котором идут сразу несколько физических явлений. */
const МОЛНИЯ: { что: string; вид: Вид }[] = [
  { что: 'видим вспышку', вид: 'световое' },
  { что: 'слышим гром', вид: 'звуковое' },
  { что: 'дерево загорается от высокой температуры', вид: 'тепловое' },
  { что: 'ломается электроника рядом', вид: 'электромагнитное' },
  { что: 'туча движется к холму', вид: 'механическое' },
]

export default function PhenomenaLab() {
  const [ответы, setОтветы] = useState<Record<number, Вид>>({})
  const [молния, setМолния] = useState<Record<number, Вид>>({})

  const верных = ЯВЛЕНИЯ.filter((я, i) => ответы[i] === я.вид).length
  const отвечено = Object.keys(ответы).length
  const молнияВсё = МОЛНИЯ.every((м, i) => молния[i] === м.вид)

  return (
    <div className="card">
      <div className="lab-caption">Отнеси каждое явление к виду. Подсказка: смотри, что именно меняется.</div>
      <div className="lab-sort">
        {ЯВЛЕНИЯ.map((я, i) => {
          const выбор = ответы[i]
          return (
            <div key={я.что} className="lab-sort-row">
              <div className="lab-sort-q">{я.что}</div>
              <div className="row-wrap lab-choice">
                {ВИДЫ.map((в) => (
                  <button
                    key={в}
                    className={выбор === в ? (в === я.вид ? 'btn btn-primary' : 'btn lab-wrong') : 'btn'}
                    onClick={() => setОтветы((x) => ({ ...x, [i]: в }))}
                  >
                    {в}
                  </button>
                ))}
              </div>
              {выбор && (
                <div className={выбор === я.вид ? 'lab-verdict ok' : 'lab-verdict'}>
                  {выбор === я.вид ? `✅ Да: ${я.почему}. Это изучает ${РАЗДЕЛ[я.вид]}.` : 'Подумай ещё: что именно здесь меняется?'}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div className="lab-big">
        Верно: <b>{верных}</b> из {ЯВЛЕНИЯ.length}
        {отвечено === ЯВЛЕНИЯ.length && верных === ЯВЛЕНИЯ.length && ' — отлично!'}
      </div>

      <div className="lab-sub">Молния — явление природы. Разложи её «по полочкам»</div>
      <div className="lab-sort">
        {МОЛНИЯ.map((м, i) => (
          <div key={м.что} className="lab-sort-row">
            <div className="lab-sort-q">⚡ {м.что}</div>
            <div className="row-wrap lab-choice">
              {ВИДЫ.map((в) => (
                <button
                  key={в}
                  className={молния[i] === в ? (в === м.вид ? 'btn btn-primary' : 'btn lab-wrong') : 'btn'}
                  onClick={() => setМолния((x) => ({ ...x, [i]: в }))}
                >
                  {в}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Predict
        question="Молния — это одно физическое явление?"
        options={['да, электрическое', 'нет, несколько разных']}
        right={1}
        revealed={молнияВсё}
        explain="Молния — явление природы, в котором одновременно идут световые, звуковые, тепловые, электромагнитные и механические явления. Физика изучает каждое по отдельности, своим разделом."
      />
      <p className="muted small">
        Физика — наука о явлениях неживой природы. Механические явления изучает механика,
        тепловые — термодинамика, звуковые — акустика, световые — оптика, электромагнитные — электродинамика.
      </p>
    </div>
  )
}
